import { WorkCalendar } from "@/engine/calendar";
import { computeSchedule } from "@/engine/cpm";
import { computeEvm } from "@/engine/health";
import { addDays, addMonthsDay, fromDay, startOfMonthDay, toDay, type ISODate } from "@/engine/date";
import type { Project, Schedule } from "@/engine/types";
import { activeBaselineOf, REPORT_RULES } from "@/features/reports/report-model";
import { fmtSpi } from "@/lib/format";
import { collectExecutionTasks } from "@/features/tasks/my-tasks";
import { dueBucket } from "@/features/tasks/task-query";
import { formatDate } from "@/lib/date-format";

/** `groupHomeTasks`가 따로 한도를 받지 않을 때 남기는 줄 수. 홈 탭은 목록 전체를 보여 준다. */
export const HOME_TASK_LIMIT = 8;

export type HomeTaskFocus = "overdue" | "week";

export type HomePlanLevel = "risk" | "watch";
export type HomePlanHealthLevel = "good" | "watch" | "risk";

export interface HomeTaskRow {
  projectId: string;
  projectName: string;
  taskId: string;
  title: string;
  due: ISODate;
  overdue: boolean;
}

export interface HomePlanRow {
  id: string;
  name: string;
  level: HomePlanLevel;
  finish: ISODate;
  progress: number;
  spi?: number | null;
  varianceDays?: number;
  hasBaseline?: boolean;
  blockedCount?: number;
  overdueCount?: number;
  reason?: string;
}

export interface HomePlanSummaryRow {
  id: string;
  name: string;
  level: HomePlanHealthLevel;
  finish: ISODate;
  progress: number;
  spi: number | null;
  varianceDays: number;
  hasBaseline: boolean;
  blockedCount: number;
  overdueCount: number;
  reason: string;
}

export interface HomeDayCount {
  key: string;
  date: ISODate | "overdue";
  label: string;
  calloutTitle: string;
  count: number;
  isOverdue: boolean;
}

export interface HomeSnapshot {
  overdue: number;
  dueThisWeek: number;
  attentionPlans: number;
  mineOpen: number;
  tasks: HomeTaskRow[];
  plans: HomePlanRow[];
  allPlans: HomePlanSummaryRow[];
  planCounts: { good: number; watch: number; risk: number; total: number };
  weekDays: HomeDayCount[];
}

/** 현황의 일정 진척과 같은 가중 평균. */
function progressOf(schedule: Schedule): number {
  let weight = 0;
  let acc = 0;
  for (const id of schedule.order) {
    const task = schedule.tasks.get(id);
    if (!task || task.isSummary) continue;
    const w = Math.max(task.duration, 0.5);
    weight += w;
    acc += w * task.percentComplete;
  }
  return weight ? Math.round(acc / weight) : 0;
}

function summarizePlan(project: Project, today: ISODate): HomePlanSummaryRow | null {
  let schedule: Schedule;
  try {
    schedule = computeSchedule(project, new WorkCalendar(project.calendar));
  } catch {
    return null;
  }
  const progress = progressOf(schedule);
  const calendar = new WorkCalendar(project.calendar);
  const evm = computeEvm(project, schedule, activeBaselineOf(project), calendar);
  const slipped = progress < 100 && schedule.projectFinish < today;
  const blockedCount = project.executionTasks.filter((t) => t.status === "blocked").length;
  const overdueCount = project.executionTasks.filter((t) => t.status !== "done" && Boolean(t.due && t.due < today)).length;
  let level: HomePlanHealthLevel = "good";
  if (evm.hasBaseline) {
    const risk = (evm.spi !== null && evm.spi < REPORT_RULES.spiRisk) || evm.finishVarianceDays > REPORT_RULES.finishRiskDays;
    const watch = (evm.spi !== null && evm.spi < REPORT_RULES.spiWatch) || evm.finishVarianceDays > 0;
    if (risk || slipped) level = "risk";
    else if (watch) level = "watch";
  } else if (slipped) {
    level = "risk";
  }
  const reasons: string[] = [];
  if (slipped) reasons.push("종료일 경과");
  if (evm.hasBaseline && evm.finishVarianceDays > 0) reasons.push(`기준선 +${evm.finishVarianceDays}일`);
  if (evm.hasBaseline && evm.spi !== null && evm.spi < REPORT_RULES.spiWatch) reasons.push(`SPI ${fmtSpi(evm.spi)}`);
  if (blockedCount > 0) reasons.push(`막힘 ${blockedCount}건`);
  if (overdueCount > 0) reasons.push(`마감 초과 ${overdueCount}건`);
  return {
    id: project.id,
    name: project.name,
    level,
    finish: schedule.projectFinish,
    progress,
    spi: evm.spi,
    varianceDays: evm.finishVarianceDays,
    hasBaseline: evm.hasBaseline,
    blockedCount,
    overdueCount,
    reason: reasons.length > 0 ? reasons.join(" · ") : evm.hasBaseline ? `SPI ${fmtSpi(evm.spi)} · 정상` : "기준선 미저장",
  };
}

/**
 * 오늘 손댈 곳. 작업은 나에게 배정된 미완료만, 플랜은 현황과 같은 일정 기준(SPI·종료 편차)이거나
 * 종료일이 지났는데 진척이 100%가 아닌 것만 담는다.
 */
export function buildHomeSnapshot(catalog: Record<string, Project>, open: Project | null, me: string, today: ISODate): HomeSnapshot {
  const projects = { ...catalog };
  if (open && projects[open.id]) projects[open.id] = open;

  const mine = collectExecutionTasks(projects, null).filter((item) => item.task.status !== "done" && item.task.assignees.includes(me));
  const ranked = mine
    .map((item) => ({ item, bucket: dueBucket(item.task, today) }))
    .filter((row) => row.bucket === "overdue" || row.bucket === "today" || row.bucket === "week")
    .sort((a, b) => {
      const rank = (bucket: string) => (bucket === "overdue" ? 0 : 1);
      const byRank = rank(a.bucket) - rank(b.bucket);
      if (byRank !== 0) return byRank;
      return (a.item.task.due ?? "").localeCompare(b.item.task.due ?? "");
    });

  const tasks: HomeTaskRow[] = ranked.map(({ item, bucket }) => ({
    projectId: item.projectId,
    projectName: item.projectName,
    taskId: item.task.id,
    title: item.task.title,
    due: item.task.due!,
    overdue: bucket === "overdue",
  }));

  const levelRank: Record<HomePlanHealthLevel, number> = { risk: 0, watch: 1, good: 2 };
  const allPlans = Object.values(projects)
    .map((project) => summarizePlan(project, today))
    .filter((row): row is HomePlanSummaryRow => row !== null)
    .sort((a, b) => {
      if (a.level !== b.level) return levelRank[a.level] - levelRank[b.level];
      return a.finish.localeCompare(b.finish) || a.name.localeCompare(b.name, "ko");
    });

  const plans: HomePlanRow[] = allPlans
    .filter((row): row is HomePlanSummaryRow & { level: HomePlanLevel } => row.level === "risk" || row.level === "watch")
    .map((row) => ({ ...row, level: row.level }));

  const planCounts = {
    good: allPlans.filter((p) => p.level === "good").length,
    watch: allPlans.filter((p) => p.level === "watch").length,
    risk: allPlans.filter((p) => p.level === "risk").length,
    total: allPlans.length,
  };

  const overdueCount = tasks.filter((task) => task.overdue).length;
  const weekDays: HomeDayCount[] = [
    {
      key: "overdue",
      date: "overdue",
      label: "지연",
      calloutTitle: "기한 지남",
      count: overdueCount,
      isOverdue: true,
    },
    ...Array.from({ length: 8 }, (_, index) => {
      const date = addDays(today, index);
      const count = tasks.filter((t) => !t.overdue && t.due === date).length;
      return {
        key: date,
        date,
        label: index === 0 ? "오늘" : formatDate(date, "short"),
        calloutTitle: `${formatDate(date, "fullWeekday")}${index === 0 ? " (오늘)" : ""}`,
        count,
        isOverdue: false,
      };
    }),
  ];

  return {
    overdue: overdueCount,
    dueThisWeek: tasks.filter((task) => !task.overdue).length,
    attentionPlans: plans.length,
    mineOpen: mine.length,
    tasks,
    plans,
    allPlans,
    planCounts,
    weekDays,
  };
}

export type HomeGateKind = "milestone" | "deadline";

export interface HomeGate {
  key: string;
  projectId: string;
  projectName: string;
  kind: HomeGateKind;
  refId: string;
  title: string;
  date: ISODate;
  /** 마감이 지났거나, 끝나지 않은 마일스톤이 오늘보다 이전이다. */
  late: boolean;
  done: boolean;
}

/**
 * 마일스톤과 마감 전체. 작업 막대와 Task는 넣지 않는다.
 * 마일스톤 날짜와 그 작업의 마감이 같으면 마일스톤 한 줄이다.
 */
export function homeGates(catalog: Record<string, Project>, open: Project | null, today: ISODate): HomeGate[] {
  const projects = { ...catalog };
  if (open && projects[open.id]) projects[open.id] = open;
  const gates: HomeGate[] = [];

  for (const project of Object.values(projects)) {
    let schedule: Schedule;
    try {
      schedule = computeSchedule(project, new WorkCalendar(project.calendar));
    } catch {
      continue;
    }
    for (const id of schedule.order) {
      const scheduled = schedule.tasks.get(id);
      const task = project.tasks.find((item) => item.id === id);
      if (!scheduled || !task) continue;
      if (scheduled.isMilestone) {
        const done = scheduled.percentComplete >= 100;
        gates.push({
          key: `${project.id}:milestone:${id}`,
          projectId: project.id,
          projectName: project.name,
          kind: "milestone",
          refId: id,
          title: task.name || "이름 없음",
          date: scheduled.start,
          late: !done && scheduled.start < today,
          done,
        });
      }
      if (!task.deadline) continue;
      if (scheduled.isMilestone && scheduled.start === task.deadline) continue;
      gates.push({
        key: `${project.id}:deadline:${id}`,
        projectId: project.id,
        projectName: project.name,
        kind: "deadline",
        refId: id,
        title: task.name || "이름 없음",
        date: task.deadline,
        late: scheduled.deadlineMissed,
        done: false,
      });
    }
  }

  gates.sort((a, b) => a.date.localeCompare(b.date) || a.projectName.localeCompare(b.projectName, "ko") || a.title.localeCompare(b.title, "ko"));
  return gates;
}

/** 홈 제목. 기본 이름 「나」는 부르지 않는다. */
export function homeGreeting(name: string): string {
  const who = name.trim();
  if (!who || who === "나") return "안녕하세요";
  return `안녕하세요, ${who}`;
}

/** 제목 아래 규모. 탭·범위를 바꿔도 같다. 이번 주·지연·주의가 없으면 그 말은 뺀다. */
export function homeScale(
  snapshot: Pick<HomeSnapshot, "planCounts" | "mineOpen" | "dueThisWeek" | "overdue" | "attentionPlans">,
  joined = snapshot.planCounts.total,
): string {
  const parts = [`참여 ${joined}개`, `미완료 ${snapshot.mineOpen}건`];
  if (snapshot.dueThisWeek > 0) parts.push(`이번 주 ${snapshot.dueThisWeek}건`);
  if (snapshot.overdue > 0) parts.push(`지연 ${snapshot.overdue}건`);
  if (snapshot.attentionPlans > 0) parts.push(`주의 ${snapshot.attentionPlans}개`);
  return parts.join(" · ");
}

export type HomeTaskScope = "week" | "overdue";

/** 마감 세그먼트의 처음 값. 고른 값이 있으면 그대로 둔다. 이번 주가 없고 지연이 있으면 지연이다. */
export function homeDueScope(chosen: HomeTaskScope | null, dueThisWeek: number, overdue: number): HomeTaskScope {
  if (chosen) return chosen;
  return dueThisWeek === 0 && overdue > 0 ? "overdue" : "week";
}

/** 보이는 달의 1일. 이전·다음은 그 달에서 한 달이다. */
export function shiftHomeMonth(anchor: ISODate, dir: -1 | 1): ISODate {
  const first = fromDay(startOfMonthDay(toDay(anchor)));
  return fromDay(addMonthsDay(toDay(first), dir));
}

export type HomeDueGroup = "overdue" | "today" | "tomorrow" | "later";

const GROUP_LABEL: Record<HomeDueGroup, string> = {
  overdue: "지연",
  today: "오늘",
  tomorrow: "내일",
  later: "이후",
};

export function homeTaskGroup(due: ISODate, overdue: boolean, today: ISODate): HomeDueGroup {
  if (overdue || due < today) return "overdue";
  if (due === today) return "today";
  if (due === addDays(today, 1)) return "tomorrow";
  return "later";
}

/** 내 마감 목록. 범위 또는 선택한 날만 남기고, 오늘·내일·이후로 묶는다. */
export function groupHomeTasks(
  tasks: readonly HomeTaskRow[],
  today: ISODate,
  scope: HomeTaskScope,
  dayKey?: string | null,
  limit = HOME_TASK_LIMIT,
): { groups: { key: HomeDueGroup; label: string; rows: HomeTaskRow[] }[]; rest: number } {
  const picked = dayKey
    ? dayKey === "overdue"
      ? tasks.filter((task) => task.overdue)
      : tasks.filter((task) => !task.overdue && task.due === dayKey)
    : scope === "overdue"
      ? tasks.filter((task) => task.overdue)
      : tasks.filter((task) => !task.overdue);

  const buckets: Record<HomeDueGroup, HomeTaskRow[]> = { overdue: [], today: [], tomorrow: [], later: [] };
  for (const row of picked) buckets[homeTaskGroup(row.due, row.overdue, today)].push(row);

  const order: HomeDueGroup[] = dayKey === "overdue" || (!dayKey && scope === "overdue") ? ["overdue"] : ["today", "tomorrow", "later"];
  let room = limit;
  let rest = 0;
  const groups: { key: HomeDueGroup; label: string; rows: HomeTaskRow[] }[] = [];
  for (const key of order) {
    const rows = buckets[key];
    if (rows.length === 0) continue;
    const take = rows.slice(0, room);
    rest += rows.length - take.length;
    room -= take.length;
    if (take.length > 0) groups.push({ key, label: GROUP_LABEL[key], rows: take });
  }
  return { groups, rest };
}

export function sliceHomeTasks(
  tasks: readonly HomeTaskRow[],
  focus: HomeTaskFocus | null,
  dayKey?: string | null,
): { rows: HomeTaskRow[]; rest: number } {
  const byFocus = focus === "overdue" ? tasks.filter((task) => task.overdue) : focus === "week" ? tasks.filter((task) => !task.overdue) : tasks;
  const filtered = dayKey
    ? dayKey === "overdue"
      ? byFocus.filter((task) => task.overdue)
      : byFocus.filter((task) => !task.overdue && task.due === dayKey)
    : byFocus;
  return { rows: filtered.slice(0, HOME_TASK_LIMIT), rest: Math.max(0, filtered.length - HOME_TASK_LIMIT) };
}
